import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default branding
const defaultBranding = {
  practiceName: 'MdSpa360',
  logoUrl: null,
  primaryColor: 'hsl(25, 35%, 75%)', // Peachy beige
  secondaryColor: 'hsl(0, 0%, 96%)', // Light grey
  accentColor: 'hsl(30, 30%, 88%)', // Warm beige
  contactEmail: 'hello@mdspa360.com',
  contactPhone: '(555) 123-4567',
  address: '123 Wellness Ave, Suite 100',
  city: 'Los Angeles',
  state: 'CA',
  zipCode: '90001',
  website: 'https://mdspa360.com',
  description: 'Premium aesthetic and wellness treatments',
  subdomain: 'demo',
  // White-label customization
  chatbotName: 'Assistant',
  chatbotWelcomeMessage: "Hi! I'm your AI Beauty Advisor. How can I help you today?",
  boulevardBusinessId: '',
  // Social media links
  facebookUrl: '',
  instagramUrl: '',
  twitterUrl: '',
  tiktokUrl: '',
  youtubeUrl: '',
  linkedinUrl: '',
  // Business hours
  businessHours: 'Mon-Sat: 9am-7pm',
  // External links (optional)
  blogUrl: '',
  reviewsUrl: '',
  financingUrl: 'https://www.carecredit.com/apply/'
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(defaultBranding);
  const [loading, setLoading] = useState(true);
  const [practiceId, setPracticeId] = useState(null);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      // In production, we'd get the subdomain from window.location.hostname
      // For now, we'll use a demo practice ID
      const subdomain = window.location.hostname.split('.')[0] || 'demo';
      
      const response = await axios.get(`${API}/branding/${subdomain}`);
      if (response.data) {
        setBranding({ ...defaultBranding, ...response.data });
        setPracticeId(response.data.practiceId);
      }
    } catch (error) {
      console.log('Using default branding', error);
      // Use default branding if no custom branding found
    } finally {
      setLoading(false);
    }
  };

  const updateBranding = async (updates) => {
    try {
      const response = await axios.put(`${API}/branding/${practiceId}`, updates);
      setBranding({ ...branding, ...response.data });
      return { success: true };
    } catch (error) {
      console.error('Error updating branding:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    branding,
    loading,
    practiceId,
    updateBranding,
    refreshBranding: fetchBranding
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};
